import React, { useContext, useState } from 'react';

import { dashboardInvoiceApis } from '@apis/next/invoiceApis';
import { BookingDetailsContext, invoiceDetailsSearchType } from '@components/admin/booking/helpers';
import { InvoiceFormDialog } from '@components/admin/booking/InvoiceFormDialog';
import { InvoiceTable } from '@components/admin/booking/InvoiceTable';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogActions,
} from '@components/Dialog';
import { QueryContainer } from '@components/QueryContainer';
import { SpinnerModal } from '@components/SpinnerModal';
import { VerticalSpace } from '@components/VerticalSpace';
import { usePatchBookingInquiry } from '@hooks/mutation/dashboard/usePatchBookingInquiry';
import { useTrackingData } from '@hooks/queries/dashboard/useTrackingData';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useDialogKey } from '@hooks/useDialogKey';
import { useSearch } from '@hooks/useSearch';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import { makeMuiStyles } from '@styles';
import { getConfig } from '@utils/config';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';

const strings = createStrings({
  ja: {
    createInvoice: '請求書を作成する',
    exportInvoice: '請求書をエクスポート',
    finishInvoices: '請求書全部発行完了',
    needsDeliveredStatus: 'お届け完了前に請求書を全部発行したことにできません。',
    finishInvoicesAlert: (is_payment_slip_done: boolean) => (
      <>
        この案件に関連するすべての請求書を作成して発行しましたか？
        <br />
        {is_payment_slip_done &&
          '「はい」を押すと、この案件を「過去の案件」に移動するので、ご了承ください。'}
      </>
    ),
    cannotCreateInvoiceDueToFCChina:
      'FC China 不可以作成请求书。只有 FC Japan 和管理员可以作成请求书。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  invoiceOpBox: {
    ...common.flexInline,
    ...common.horizontalSpace1,
  },
  paper: {
    maxWidth: 960,
  },
  dialogContent: {
    paddingTop: 30,
    paddingBottom: 120,
  },
}));

export const InvoicePanel: React.FC = () => {
  const classes = {
    ...useStyles(),
  };
  const { bookingInquiry, preAlert, user } = useContext(BookingDetailsContext);

  const { search, updateSearch } = useSearch(invoiceDetailsSearchType);

  const confirmDialog = useConfirmDialog();

  const invoiceQuery = dashboardInvoiceApis.useGet('/dashboard/invoice', {
    queries: {
      booking_inquiry: bookingInquiry.id,
    },
  });

  const trackingData = useTrackingData(bookingInquiry.d2d_no);

  const patchBookingInquiry = usePatchBookingInquiry(bookingInquiry.d2d_no);

  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [editInvoiceDialogOpen, setEditInvoiceDialogOpen] = useState(false);
  const [editInvoiceId, setEditInvoiceId] = useState(0);
  const [finishInvoicesOpen, setFinishInvoicesOpen] = useState(false);

  const invoiceDialogKey = useDialogKey(invoiceDialogOpen);
  const editInvoiceDialogKey = useDialogKey(editInvoiceDialogOpen);

  const renderFinishInvoicesDialog = () => (
    <ActionableDialog
      open={finishInvoicesOpen}
      classes={{
        paper: classes.paper,
      }}
      onClose={() => setFinishInvoicesOpen(false)}
    >
      <ActionableDialogTitle>{strings.finishInvoices}</ActionableDialogTitle>
      <ActionableDialogContent className={classes.dialogContent}>
        {strings.finishInvoicesAlert(bookingInquiry.is_payment_slip_done)}
      </ActionableDialogContent>
      <ActionableDialogActions>
        <Button
          color="grey"
          size="small"
          text={formStrings.cancel}
          onClick={() => setFinishInvoicesOpen(false)}
          type="button"
        />
        <Button
          color="primary"
          size="small"
          text={formStrings.yes}
          onClick={async () => {
            await patchBookingInquiry.mutateAsync({
              is_invoice_done: true,
            });

            setFinishInvoicesOpen(false);
          }}
          type="button"
        />
      </ActionableDialogActions>
    </ActionableDialog>
  );

  return (
    <>
      <QueryContainer queries={[invoiceQuery, trackingData]}>
        {() => {
          const trackingDataData =
            trackingData.data && 'data' in trackingData.data ? trackingData.data : ({} as never);

          const canFinishInvoices = InvoiceHelper.canFinishInvoices(
            user,
            bookingInquiry,
            trackingDataData,
          );

          const canEdit = InvoiceHelper.canCreateInvoice(user, preAlert);

          return (
            <>
              {invoiceQuery.data?.results && invoiceQuery.data.results.length > 0 && (
                <InvoiceTable
                  bookingInquiry={bookingInquiry}
                  items={invoiceQuery.data?.results}
                  checklist={search.selectedInvoiceIds ?? []}
                  onChecklistChange={(selectedInvoiceIds) => updateSearch({ selectedInvoiceIds })}
                />
              )}

              <InvoiceFormDialog
                key={invoiceDialogKey}
                type="create"
                open={invoiceDialogOpen}
                onClose={() => setInvoiceDialogOpen(false)}
                onEditInvoice={(id) => {
                  setEditInvoiceDialogOpen(true);
                  setEditInvoiceId(id);
                  setInvoiceDialogOpen(false);
                }}
              />

              {editInvoiceId ? (
                <InvoiceFormDialog
                  key={editInvoiceDialogKey}
                  id={editInvoiceId}
                  type="update"
                  open={editInvoiceDialogOpen}
                  onClose={() => setInvoiceDialogOpen(true)}
                />
              ) : null}

              {confirmDialog.renderDialog()}

              <VerticalSpace height={16} />

              <div className={classes.invoiceOpBox}>
                <Button
                  size="small"
                  shape="round"
                  text={strings.createInvoice}
                  color={foldMessagesEither(
                    canEdit,
                    () => 'grey',
                    () => 'primary',
                  )}
                  onClick={() =>
                    foldMessagesEither(
                      canEdit,
                      ({ messages }) => {
                        confirmDialog.confirm({ message: messages });
                      },
                      () => {
                        setInvoiceDialogOpen(true);
                      },
                    )
                  }
                />
                <Button
                  size="small"
                  shape="round"
                  text={strings.exportInvoice}
                  color="primary"
                  disabled={
                    (invoiceQuery.data?.results.length ?? 0) === 0 ||
                    !search.selectedInvoiceIds?.length
                  }
                  onClick={() => {
                    if ((search.selectedInvoiceIds?.length ?? 0) > 1) {
                      window.open(
                        `${
                          getConfig().serverUrl
                        }/invoice/v3/download-zip/?${search.selectedInvoiceIds
                          ?.map((id) => `selected=${id}`)
                          .join('&')}`,
                      );
                    } else {
                      window.open(
                        `${getConfig().serverUrl}/invoice/v3/${
                          search.selectedInvoiceIds?.[0]
                        }/download/`,
                      );
                    }
                  }}
                />
                <Button
                  size="small"
                  shape="round"
                  text={strings.finishInvoices}
                  color={foldMessagesEither(
                    canFinishInvoices,
                    () => 'grey',
                    () => 'primary',
                  )}
                  onClick={async () => {
                    if (canFinishInvoices._tag === 'Left') {
                      await confirmDialog.confirm({
                        message: canFinishInvoices.left.messages.join('\n'),
                      });
                    } else if (
                      (await confirmDialog.confirm({
                        message: strings.finishInvoicesAlert(bookingInquiry.is_payment_slip_done),
                      })) === 'confirmed'
                    ) {
                      await patchBookingInquiry.mutateAsync({
                        is_invoice_done: true,
                      });
                    }
                  }}
                />
              </div>
            </>
          );
        }}
      </QueryContainer>
      <SpinnerModal open={patchBookingInquiry.isLoading} />
      {renderFinishInvoicesDialog()}
    </>
  );
};
